/**
 * AchatsDemandesPubliques.js
 * Page ultra professionnelle pour l'achat de demandes publiques COOPER'S
 * Design moderne avec statistiques, filtres avancés et cartes premium
 */

import React from 'react';

import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';

import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

import { ShoppingCart, CheckCircle, Clock, Euro, Calendar, X, Search, MapPin, FileText, TrendingUp, ArrowLeft, RefreshCw, Briefcase, ChevronDown, ChevronUp, Package } from 'lucide-react';

const _API_URL = process.env.REACT_APP_BACKEND_URL;

// V37.28 — Activités professionnelles (doit correspondre à la constante
// `ACTIVITES_PROFESSIONNELLES` de GestionTarifsDemandesPubliques.js).
const ACTIVITES_PROFESSIONNELLES = [
  { value: 'btp', label: 'BTP / Construction', icon: '🏗️' },
  { value: 'commerce', label: 'Commerce / Distribution', icon: '🏪' },
  { value: 'industrie', label: 'Industrie / Manufacturing', icon: '🏭' },
  { value: 'agriculture', label: 'Agriculture / Élevage', icon: '🌾' },
  { value: 'services', label: 'Services / Prestations', icon: '🛠️' },
  { value: 'transport', label: 'Transport / Logistique', icon: '🚚' },
  { value: 'restauration', label: 'Restauration / Hôtellerie', icon: '🍽️' },
  { value: 'sante', label: 'Santé / Médico-social', icon: '⚕️' },
  { value: 'education', label: 'Éducation / Formation', icon: '🎓' },
  { value: 'numerique', label: 'Numérique / IT', icon: '💻' },
  { value: 'artisanat', label: 'Artisanat', icon: '🎨' },
  { value: 'tourisme', label: 'Tourisme / Loisirs', icon: '🏝️' },
  { value: 'energie', label: 'Énergie / Environnement', icon: '⚡' },
  { value: 'immobilier', label: 'Immobilier', icon: '🏢' },
  { value: 'finance', label: 'Finance / Assurance', icon: '💰' },
  { value: 'autre', label: 'Autre', icon: '📋' },
];

// V37.28 — Extrait l'array d'activites d'une demande (compat retro string)
const _getDemandeActivites = (d) => {
  if (Array.isArray(d?.activites) && d.activites.length > 0) {
    return d.activites.map(v => (v || '').toString().toLowerCase().trim()).filter(Boolean);
  }
  const raw = (d?.secteur || '').toString().trim().toLowerCase();
  if (!raw || raw === 'general') return [];
  return raw.split(/[,;|]/).map(s => s.trim()).filter(Boolean);
};

import { useAchats } from './useAchats';
import AchatsContent from './AchatsContent';
import AchatsModals from './AchatsModals';

const AchatsDemandesPubliques = () => {
  const ctx = useAchats();
  const { activeTab, demandes: demandes, demandesFiltrees, fetchData, filtreActivites, filtrePeriode, filtreTerritoire, filtreType, formatMontant, hasActiveFilters, loading, mesAchats, navigate, resetFiltres, searchQuery, setActiveTab, setFiltreActivites, setFiltrePeriode, setFiltreTerritoire, setFiltreType, setSearchQuery, setShowActivitesPanel, showActivitesPanel, stats, tarifInfo, territoiresDisponibles, toggleActiviteFilter, typesDisponibles } = ctx;
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600 font-medium">Chargement des demandes...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        
        {/* Header Premium */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="mb-4 text-slate-600 hover:text-slate-900 hover:bg-white/50"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour au dashboard
          </Button>
          
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg">
                  <ShoppingCart className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                    Communityplace Demandes
                  </h1>
                  <p className="text-slate-600">Développez votre portefeuille clients</p>
                </div>
              </div>
            </div>
            
            {/* Tarif Info Card */}
            {tarifInfo && (
              <Card className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white border-0 shadow-xl">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <Euro className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-indigo-100 text-sm">Prix par demande</p>
                      <p className="text-2xl font-bold">{formatMontant(tarifInfo.prix_ttc || tarifInfo.prix_unitaire_ttc)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="bg-white/80 backdrop-blur border-0 shadow-lg hover:shadow-xl transition-all">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <Package className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.totalDisponibles}</p>
                  <p className="text-xs text-slate-500">Disponibles</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/80 backdrop-blur border-0 shadow-lg hover:shadow-xl transition-all">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <ShoppingCart className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.totalAchats}</p>
                  <p className="text-xs text-slate-500">Mes achats</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/80 backdrop-blur border-0 shadow-lg hover:shadow-xl transition-all">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.achatsConfirmes}</p>
                  <p className="text-xs text-slate-500">Confirmés</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/80 backdrop-blur border-0 shadow-lg hover:shadow-xl transition-all">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Clock className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.achatsEnAttente}</p>
                  <p className="text-xs text-slate-500">En attente</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white border-0 shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <TrendingUp className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xl font-bold">{formatMontant(stats.montantTotal)}</p>
                  <p className="text-xs text-emerald-100">Investi</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="flex gap-2 p-1 bg-white/60 backdrop-blur rounded-xl w-fit">
            <button
              onClick={() => setActiveTab('disponibles')}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'disponibles'
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                  : 'text-slate-600 hover:bg-white/50'
              }`}
            >
              <Package className="h-4 w-4" />
              Disponibles
              <Badge className="ml-1 bg-indigo-700 text-white font-semibold">{demandesFiltrees.length}</Badge>
            </button>
            <button
              onClick={() => setActiveTab('mes-achats')}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'mes-achats'
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                  : 'text-slate-600 hover:bg-white/50'
              }`}
            >
              <ShoppingCart className="h-4 w-4" />
              Mes achats
              <Badge className="ml-1 bg-purple-700 text-white font-semibold">{mesAchats.length}</Badge>
            </button>
          </div>
        </div>

        {/* Filtres */}
        <Card className="mb-6 bg-white/80 backdrop-blur border-0 shadow-lg">
          <CardContent className="p-4">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Recherche */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Rechercher une demande..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-white/50 border-slate-200 focus:border-indigo-500"
                />
              </div>

              {/* Filtres rapides */}
              <div className="flex gap-2 flex-wrap">
                <Select value={filtreType} onValueChange={setFiltreType}>
                  <SelectTrigger className="w-40 bg-white/50">
                    <FileText className="h-4 w-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {typesDisponibles.map(type => (
                      <SelectItem key={type} value={type}>
                        {type === 'tous' ? 'Tous les types' : type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filtreTerritoire} onValueChange={setFiltreTerritoire}>
                  <SelectTrigger className="w-44 bg-white/50">
                    <MapPin className="h-4 w-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Territoire" />
                  </SelectTrigger>
                  <SelectContent>
                    {territoiresDisponibles.map(t => (
                      <SelectItem key={t} value={t}>
                        {t === 'tous' ? 'Tous territoires' : t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filtrePeriode} onValueChange={setFiltrePeriode}>
                  <SelectTrigger className="w-40 bg-white/50">
                    <Calendar className="h-4 w-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Période" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tout">Toutes périodes</SelectItem>
                    <SelectItem value="aujourd-hui">Aujourd'hui</SelectItem>
                    <SelectItem value="semaine">Cette semaine</SelectItem>
                    <SelectItem value="mois">Ce mois</SelectItem>
                  </SelectContent>
                </Select>

                {/* V37.28 — Bouton multi-select Activités professionnelles */}
                <Button
                  variant="outline"
                  onClick={() => setShowActivitesPanel((v) => !v)}
                  data-testid="toggle-activites-filter"
                  className={`bg-white/50 ${filtreActivites.length > 0 ? 'border-amber-500 text-amber-700 font-semibold' : 'text-slate-700'}`}
                >
                  <Briefcase className="h-4 w-4 mr-2" />
                  Activités
                  {filtreActivites.length > 0 && (
                    <Badge className="ml-2 bg-amber-600 text-white px-1.5 py-0 text-[10px] font-semibold" data-testid="activites-filter-count">
                      {filtreActivites.length}
                    </Badge>
                  )}
                  {showActivitesPanel ? <ChevronUp className="h-3.5 w-3.5 ml-1.5" /> : <ChevronDown className="h-3.5 w-3.5 ml-1.5" />}
                </Button>

                {hasActiveFilters && (
                  <Button variant="outline" onClick={resetFiltres} className="text-red-600 hover:bg-red-50">
                    <X className="h-4 w-4 mr-1" />
                    Réinitialiser
                  </Button>
                )}

                <Button variant="outline" onClick={fetchData} className="text-indigo-600 hover:bg-indigo-50">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* V37.28 — Panel multi-select Activités professionnelles */}
            {showActivitesPanel && (
              <div className="mt-4 pt-4 border-t border-slate-200" data-testid="activites-filter-panel">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
                    <Briefcase className="h-3.5 w-3.5 text-amber-600" /> Filtrer par activité professionnelle
                  </div>
                  {filtreActivites.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setFiltreActivites([])}
                      data-testid="clear-activites-filter"
                      className="text-[11px] text-red-600 hover:underline font-medium"
                    >
                      Tout désélectionner ({filtreActivites.length})
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                  {ACTIVITES_PROFESSIONNELLES.map((act) => {
                    const checked = filtreActivites.includes(act.value);
                    return (
                      <button
                        type="button"
                        key={act.value}
                        onClick={() => toggleActiviteFilter(act.value)}
                        data-testid={`activite-filter-chip-${act.value}`}
                        data-checked={checked ? 'true' : 'false'}
                        className={`group flex items-center gap-2 rounded-lg border px-2.5 py-2 text-left text-xs transition-all ${
                          checked
                            ? 'border-amber-500 bg-amber-100 text-amber-900 shadow-sm font-semibold'
                            : 'border-slate-200 bg-white hover:border-amber-300 hover:bg-amber-50 text-slate-700'
                        }`}
                      >
                        <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                          checked ? 'border-amber-600 bg-amber-600 text-white' : 'border-slate-300 bg-white'
                        }`}>
                          {checked && <CheckCircle className="h-3 w-3" />}
                        </span>
                        <span className="text-base leading-none">{act.icon}</span>
                        <span className="truncate">{act.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Filtres actifs */}
            {hasActiveFilters && (
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-200">
                <span className="text-sm text-slate-500">Filtres actifs:</span>
                {searchQuery && (
                  <Badge variant="secondary" className="flex items-center gap-1 bg-indigo-700 text-white font-semibold">
                    <Search className="h-3 w-3" />
                    "{searchQuery}"
                    <X className="h-3 w-3 cursor-pointer ml-1" onClick={() => setSearchQuery('')} />
                  </Badge>
                )}
                {filtreType !== 'tous' && (
                  <Badge variant="secondary" className="flex items-center gap-1 bg-purple-700 text-white font-semibold">
                    <FileText className="h-3 w-3" />
                    {filtreType}
                    <X className="h-3 w-3 cursor-pointer ml-1" onClick={() => setFiltreType('tous')} />
                  </Badge>
                )}
                {filtreTerritoire !== 'tous' && (
                  <Badge variant="secondary" className="flex items-center gap-1 bg-emerald-700 text-white font-semibold">
                    <MapPin className="h-3 w-3" />
                    {filtreTerritoire}
                    <X className="h-3 w-3 cursor-pointer ml-1" onClick={() => setFiltreTerritoire('tous')} />
                  </Badge>
                )}
                {filtrePeriode !== 'tout' && (
                  <Badge variant="secondary" className="flex items-center gap-1 bg-amber-700 text-white font-semibold">
                    <Calendar className="h-3 w-3" />
                    {filtrePeriode}
                    <X className="h-3 w-3 cursor-pointer ml-1" onClick={() => setFiltrePeriode('tout')} />
                  </Badge>
                )}
                {/* V37.28 — Badges pour chaque activité sélectionnée */}
                {filtreActivites.map((val) => {
                  const act = ACTIVITES_PROFESSIONNELLES.find(a => a.value === val);
                  if (!act) return null;
                  return (
                    <Badge
                      key={val}
                      variant="secondary"
                      className="flex items-center gap-1 bg-amber-700 text-white font-semibold"
                      data-testid={`active-filter-activite-${val}`}
                    >
                      <span className="text-xs">{act.icon}</span>
                      {act.label}
                      <X className="h-3 w-3 cursor-pointer ml-1" onClick={() => toggleActiviteFilter(val)} />
                    </Badge>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <AchatsContent ctx={ctx} />
      </div>
      <AchatsModals ctx={ctx} />
    </div>
  );
};

export default AchatsDemandesPubliques;
